import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import React, { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import SmCont from "../../components/containers/Sm";
import BasicForm from "../../components/textField/BasicForm";
import { SignUpFormOptions } from "../../consts/SignUpFormOptions";
import {
  validationMessage,
} from "../../consts/ValidationMessage";
import { validate } from "../../utils/userinfoValidation";
import { ValidationErrors } from "../../utils/validationUtils";

type UserDetailInfoFormStepProps = {
  onNext: () => void;
};

const UserDetailInfoFormStep: FC<UserDetailInfoFormStepProps> = ({
  onNext,
}) => {
  sessionStorage.clear();
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [isBusinessOwnerChecked, setIsBusinessOwnerChecked] = useState(false);
  const [optIn1, setOptIn1] = useState(true);
  const [optIn2, setOptIn2] = useState(true);
  const [customerKbn, setCustomerKbn] = useState("0");
  const [selectedPrefecture, setSelectedPrefecture] = useState("");
  const navigate = useNavigate();
  const [form, setForm] = useState({
    corporateName: "",
    postalCode: "",
    city: "",
    streetAddress: "",
    chargeLastName: "",
    chargeFirstName: "",
    cybozuDomain: "",
  });
  const [errors, setErrors] = useState<ValidationErrors>({
    selectedPrefecture: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'cybozuDomain') {
      newValue = value
        .replace(/^(https?:\/\/)(.*)$/, "$2")
        .replace(/^([0-9a-zA-Z][0-9a-zA-Z-]+[0-9a-zA-Z])\..*$/, "$1")
        .toLowerCase();
    } else if (name === 'postalCode') {
      // 郵便番号は数字のみにし、「3桁-4桁」形式に変換
      const digitsOnly = value.replace(/\D/g, '');
      const limitedDigits = digitsOnly.slice(0, 7);
      if (limitedDigits.length > 3) {
        newValue = `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
      } else {
        newValue = limitedDigits;
      }
    }

    setForm((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleButtonClick: () => void = (): void => {
    try {
      let isValid = true;
      const newErrors: ValidationErrors = { ...errors };

      const formValid = validate(
        form,
        (fieldErrors) => {
          Object.assign(newErrors, fieldErrors);
        },
      );

      if (!selectedPrefecture) {
        newErrors.selectedPrefecture = `都道府県${validationMessage.REQUIRED}`;
        isValid = false;
      } else {
        newErrors.selectedPrefecture = "";
      }

      setErrors(newErrors);

      if (!formValid || !isValid) {
        return;
      }

      const allFormData = {
        ...form,
        customerKbn,
        isAgreementChecked,
        prefecture: selectedPrefecture,
        optIn1,
        optIn2,
      };
      sessionStorage.setItem("userDetailInfo", JSON.stringify(allFormData));
      onNext();
    } catch {
      navigate("/error");
    }
  };

  const handleAgreementChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsAgreementChecked(event.target.checked);
  };

  const handleBusinessOwnerChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsBusinessOwnerChecked(event.target.checked);
  };

  const renderDropdownOptions = (options: { key: string; value: string }[]) => {
    return options.map((option) => (
      <MenuItem key={option.key} value={option.key}>
        {option.value}
      </MenuItem>
    ));
  };

  return (
    <SmCont>
      <Box mt={10} textAlign="center">
        <Typography variant="h5" gutterBottom>
          情報入力
        </Typography>
      </Box>
      <Box
        component="form"
        mt={5}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <FormControl>
          <FormLabel>お客様区分</FormLabel>
          <RadioGroup
            row
            value={customerKbn}
            onChange={(e): void => {
              setCustomerKbn(e.target.value);
            }}
          >
            <FormControlLabel value="0" control={<Radio />} label="法人" />
            <FormControlLabel
              value="1"
              control={<Radio />}
              label="個人事業主"
            />
            <FormControlLabel
              value="2"
              control={<Radio />}
              label="公共団体等"
            />
          </RadioGroup>
        </FormControl>
        <BasicForm
          label={"会社名・団体名・屋号"}
          name={"corporateName"}
          value={form.corporateName}
          onChange={handleChange}
          error={!!errors.corporateName}
          helperText={errors.corporateName}
        />
        <BasicForm
          label={"郵便番号"}
          name={"postalCode"}
          value={form.postalCode}
          onChange={handleChange}
          error={!!errors.postalCode}
          helperText={errors.postalCode}
          placeholder="123-4567"
        />
        <FormControl fullWidth error={!!errors.selectedPrefecture}>
          <FormLabel>都道府県</FormLabel>
          <Select
            value={selectedPrefecture}
            onChange={(e) => setSelectedPrefecture(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              選択してください
            </MenuItem>
            {renderDropdownOptions(SignUpFormOptions.prefectures)}
          </Select>
          {errors.selectedPrefecture && (
            <FormHelperText>{errors.selectedPrefecture}</FormHelperText>
          )}
        </FormControl>
        <BasicForm
          label={"市区町村"}
          name={"city"}
          value={form.city}
          onChange={handleChange}
          error={!!errors.city}
          helperText={errors.city}
          placeholder="港区"
        />
        <BasicForm
          label={"町名番地・建物名"}
          name={"streetAddress"}
          value={form.streetAddress}
          onChange={handleChange}
          error={!!errors.streetAddress}
          helperText={errors.streetAddress}
          placeholder="虎ノ門２丁目１０−１"
        />
        <Box display="flex" gap={2}>
          <BasicForm
            label={"契約担当者（姓）"}
            name={"chargeLastName"}
            value={form.chargeLastName}
            onChange={handleChange}
            error={!!errors.chargeLastName}
            helperText={errors.chargeLastName}
          />
          <BasicForm
            label={"契約担当者（名）"}
            name={"chargeFirstName"}
            value={form.chargeFirstName}
            onChange={handleChange}
            error={!!errors.chargeFirstName}
            helperText={errors.chargeFirstName}
          />
        </Box>
        <BasicForm
          label={"Cybozuドメイン"}
          name={"cybozuDomain"}
          value={form.cybozuDomain}
          onChange={handleChange}
          error={!!errors.cybozuDomain}
          helperText={errors.cybozuDomain}
        />
        <Typography variant="body2" color="textSecondary">
          例：xxxxxx.cybozu.comのxxxxxxの部分 を入力してください。
        </Typography>
      </Box>
      <Box mt={5}>
        <Box mb={2} mt={1}>
          <Typography variant="body2" color="textSecondary">
            ご登録いただいたメールアドレス宛に、当社よりサービス情報やイベント案内などをお送りいたします。不要な方はチェックを外してください。
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={optIn1}
              onChange={(e) => setOptIn1(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              仕様変更や事例紹介、ノウハウなどサービスに関する情報
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={optIn2}
              onChange={(e) => setOptIn2(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              イベント、キャンペーンに関するお知らせ
            </Typography>
          }
        />
        <Box mb={2} mt={2}>
          <Typography variant="body2" color="textSecondary">
            <Link
              href="https://kwcplus.kddi-web.com/terms-of-service"
              variant="body2"
              target="_blank"
            >
              共通利用規約
            </Link>
            、
            <Link
              href="https://kwcplus.kddi-web.com/terms-of-service/vcall"
              variant="body2"
              target="_blank"
            >
              V Callプラグイン for kintone 個別利用規約
            </Link>
            、
            <Link
              href="https://www.kddi-webcommunications.co.jp/privacy/"
              variant="body2"
              target="_blank"
            >
              プライバシーポリシー
            </Link>
            をご確認の上、チェックにて同意いただきますようお願い致します。
          </Typography>
        </Box>
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Checkbox
              checked={isAgreementChecked}
              onChange={handleAgreementChange}
              required
            />
          }
          label={"共通利用規約、V Callプラグイン for kintone 個別利用規約、プライバシーポリシーに同意する"}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isBusinessOwnerChecked}
              onChange={handleBusinessOwnerChange}
              required
            />
          }
          label={"申込者は、自らが権利能力のある法人・団体又は個人事業主であり、「V Callプラグイン for kintone」を自らの営利・業務目的で利用することを表明保証します"}
        />
        <Box mt={5}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleButtonClick}
            disabled={!isAgreementChecked || !isBusinessOwnerChecked}
          >
            登録
          </Button>
        </Box>
      </Box>
    </SmCont>
  );
};

export default UserDetailInfoFormStep;